/**
 * Main App Component
 * Routing and header navigation
 */

import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPage';
import AdminLogin from './pages/AdminLogin';
import PublicHeader from './components/PublicHeader';
import './styles.css';

/**
 * Protected Route Component
 * Redirects to login if not authenticated
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page, but save the attempted location
    return <Navigate to="/admin-login" state={{ from: location }} replace />;
  }

  return children;
};

/**
 * Admin Header Component with Logout
 */
const AdminHeader = () => {
  const { isAuthenticated, admin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin-login');
  };

  if (!isAuthenticated) return null;

  return (
    <header>
      <div className="header-left">
        <p>
          <a href="https://siragupathippagam.in/blog">Blogs</a>
        </p>
        <button className="brown-shine-btn" onClick={() => navigate('/')}>
          அகை இலக்கிய இதழ்
        </button>
      </div>

      <div className="header-center">
        <div className="loop-text">Admin Panel</div>
      </div>

      <div className="header-right">
        <h2>Siragu Pathippagam</h2>
        <p>சிறகு பதிப்பகம்</p>
        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.9rem', color: '#666' }}>
            Logged in as: <strong>{admin?.username}</strong>
          </span>
          <button
            className="logout-btn"
            onClick={handleLogout}
            title="Logout"
          >
            Logout
          </button>
        </div>
      </div>
    </header>
  );
};

/**
 * Main App Component with Auth Provider
 */
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  // Custom header for admin pages
  const AdminLayout = ({ children }) => (
    <>
      <AdminHeader />
      <main>{children}</main>
    </>
  );

  return (
    <>
      <Routes>
        {/* Public Route with PublicHeader */}
        <Route
          path="/"
          element={
            <>
              <PublicHeader />
              <HomePage />
            </>
          }
        />

        {/* Admin Login Route (no header) */}
        <Route
          path="/admin-login"
          element={
            isAuthenticated ? <Navigate to="/admin" replace /> : <AdminLogin />
          }
        />

        {/* Protected Admin Route */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
